import { PageController } from '@client/components/_core/layout/PageController';
import { HandlingPartySelect } from '@client/components/_shared/selectors/common/HandlingPartySelect';
import { UserSelector } from '@client/components/_shared/selectors/users/UserSelector';
import { Button } from '@client/components/_ui/data-entry/ButtonModern';
import { TipTapInput } from '@client/components/_ui/data-entry/TipTap';
import { errorMessage } from '@client/components/_ui/feedback/error-message/ErrorMessage';
import { message } from '@client/components/_ui/feedback/message';
import { Space } from '@client/components/_ui/layout/Space';
import { useCreateSignalStore } from '@client/context/signal';
import { useOrgId } from '@client/hooks/business/org/useOrgId';
import { useNavigate } from '@client/hooks/navigation/useNavigate';
import { useSafePath } from '@client/hooks/utils/useSafePath';
import { type RouterInputs, trpc } from '@client/trpc/client';
import { scrollToFirstFormError } from '@client/utils/ui/form';
import {
    CONTROL_FREQUENCY,
    CONTROL_STATUSES,
    CONTROL_TYPES,
    type ControlFrequency,
    type ControlStatus,
    type ControlType,
} from '@shared/constants';
import { createFileRoute } from '@tanstack/react-router';
import { Card, Col, Form, Input, Row, Select, Typography } from 'antd';
import { ControlContext, type ControlSignals } from '../../ControlContext';

export const Route = createFileRoute('/console/$orgId/controls/register/add')({
    staticData: {
        breadcrumb: {
            title: 'New control',
        },
        ui: {
            widthVariant: 'compact',
        },
    },
    component: RouteComponent,
});

function RouteComponent() {
    return (
        <PageController route={Route}>
            <ControlAdd />
        </PageController>
    );
}

type ControlInput = RouterInputs['control']['createControl'];

function ControlAdd() {
    const [form] = Form.useForm<ControlInput>();
    const orgId = useOrgId();
    const navigate = useNavigate();
    const signalStore = useCreateSignalStore<ControlSignals>();
    const { mutateAsync: createControl, isPending: createControlLoading } = trpc.control.createControl.useMutation();

    const handlingPartyIds = Form.useWatch('handlingPartyIds', form);

    const safePath = useSafePath<ControlInput>();

    const onFinish = async (values: ControlInput) => {
        try {
            message.loading('Saving...');
            const { id } = await createControl(values);
            message.success('Saved.');

            navigate({
                to: '/console/$orgId/controls/register/details/$id',
                params: {
                    orgId,
                    id,
                },
            });
        } catch (e) {
            errorMessage.show(e);
        }
    };

    return (
        <ControlContext.Provider value={signalStore}>
            <Card>
                <Typography.Title level={4} className="mt-0 mb-4">
                    Add control
                </Typography.Title>
                <Form
                    form={form}
                    id="controlEdit"
                    layout="vertical"
                    onFinish={onFinish}
                    onFinishFailed={scrollToFirstFormError('controlEdit')}
                >
                    <Row gutter={[16, 16]}>
                        <Col xs={24} sm={24} md={12} lg={8}>
                            <Form.Item label="Status" name={safePath('status')} required rules={[{ required: true }]}>
                                <Select
                                    options={Object.keys(CONTROL_STATUSES).map((key) => ({
                                        label: CONTROL_STATUSES[key as ControlStatus],
                                        value: key,
                                    }))}
                                />
                            </Form.Item>
                        </Col>

                        <Col xs={24} sm={24} md={12} lg={8}>
                            <Form.Item
                                label="Frequency"
                                name={safePath('frequency')}
                                required
                                rules={[{ required: true }]}
                            >
                                <Select
                                    options={Object.keys(CONTROL_FREQUENCY).map((key) => ({
                                        label: CONTROL_FREQUENCY[key as ControlFrequency],
                                        value: key,
                                    }))}
                                />
                            </Form.Item>
                        </Col>
                        <Col xs={24} sm={24} md={12} lg={8}>
                            <Form.Item label="Type" name={safePath('type')} required rules={[{ required: true }]}>
                                <Select
                                    options={Object.keys(CONTROL_TYPES).map((key) => ({
                                        label: CONTROL_TYPES[key as ControlType],
                                        value: key,
                                    }))}
                                />
                            </Form.Item>
                        </Col>
                        <Col xs={24} sm={24} md={12} lg={12}>
                            <Form.Item
                                label="Handling parties"
                                name={safePath('handlingPartyIds')}
                                required
                                rules={[{ required: true }]}
                            >
                                <HandlingPartySelect
                                    forOrgAdminShowAll
                                    onChange={() => form.setFieldsValue({ ownerId: undefined })}
                                />
                            </Form.Item>
                        </Col>
                        <Col xs={24} sm={24} md={12} lg={12}>
                            <Form.Item
                                label="Control owner"
                                name={safePath('ownerId')}
                                required
                                rules={[{ required: true }]}
                            >
                                <UserSelector
                                    handlingPartyIds={handlingPartyIds}
                                    placeholder=""
                                    limitToCurrentUserHandlingParties
                                />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Row gutter={[16, 16]}>
                        <Col xs={24}>
                            <Form.Item
                                label="Control name"
                                name={safePath('name')}
                                required
                                rules={[{ required: true, whitespace: true }]}
                            >
                                <Input />
                            </Form.Item>
                        </Col>
                        <Col xs={24}>
                            <Form.Item
                                label="Control objective"
                                name={safePath('objective')}
                                required
                                rules={[{ required: true }]}
                            >
                                <Input />
                            </Form.Item>
                        </Col>
                        <Col xs={24}>
                            <Form.Item
                                label="Control description"
                                name={safePath('description')}
                                required
                                rules={[{ required: true }]}
                            >
                                <TipTapInput />
                            </Form.Item>
                        </Col>
                    </Row>
                </Form>
                <Space className="flex justify-end">
                    <Button
                        type="secondary"
                        onClick={() =>
                            navigate({
                                to: '/console/$orgId/controls/register',
                                params: {
                                    orgId,
                                },
                            })
                        }
                    >
                        Cancel
                    </Button>
                    <Button type="primary" htmlType="submit" form="controlEdit" loading={createControlLoading}>
                        Save
                    </Button>
                </Space>
            </Card>
        </ControlContext.Provider>
    );
}
